import dayjs from "dayjs"
import { useAuth } from "./useAuth"
import { setError, setSuccess, startLoading, stopLoading } from "../components/Notification"
import { confirmAsync } from "../components/Confirm"

// #991 absence records. "Present" is not stored - it deletes the day.
export const ABSENCE_MODES = [
  { value: "Vacation", text: "Dop.", short: "D" },
  { value: "SickLeave", text: "Bol.", short: "B" },
  { value: "Present", text: "Pris.", short: "" },
]

export const isWeekend = (date) => date.day() === 0 || date.day() === 6

// dates from `from` to `to` (either order), weekends skipped unless only one day is selected
export const datesBetween = (from, to) => {
  let [a, b] = from.isAfter(to) ? [to, from] : [from, to]
  if (a.isSame(b, "day")) return [a]
  const result = []
  for (let d = a; !d.isAfter(b, "day"); d = d.add(1, "day")) {
    if (!isWeekend(d)) result.push(d)
  }
  return result
}

export const useAbsences = () => {
  const { fetchAuthAsync } = useAuth()

  // { today, lockDate, workers: [{ id, firstName, lastName, department, canEdit }], result: [{ workerId, date, type }] }
  const loadAbsencesAsync = async ({ from, to, workerId }) => {
    startLoading("loadAbsencesAsync")
    try {
      let url = `${AZURE_URI}/Absences?from=${from.format("YYYY-MM-DD")}&to=${to.format("YYYY-MM-DD")}`
      if (workerId) url += `&workerId=${workerId}`
      const raw = await fetchAuthAsync(url, "GET")
      if (raw?.status !== "ok") {
        throw raw
      }
      return raw
    } catch (e) {
      setError(e)
      return null
    } finally {
      stopLoading("loadAbsencesAsync")
    }
  }

  // returns the response ({ isAbsent, message }) or null on error / when not confirmed
  const setAbsencesAsync = async (workerId, dates, mode) => {
    // #1759: an absence for today takes the worker's accepted service orders away
    if (mode !== "Present" && dates.some((d) => d.isSame(dayjs(), "day"))) {
      const confirmed = await confirmAsync({
        title: "Odsotnost za današnji dan",
        message:
          "Delavcu bodo odvzeti vsi sprejeti nalogi (vrnejo se v Active), razen tistih, za katere je material že v njegovem vozilu. " +
          "Njihove odprte medskladiščnice bodo preklicane.\n\n" +
          "Če odsotnost kasneje izbrišeš, se nalogi delavcu ne vrnejo - treba jih je znova dodeliti.",
        confirmText: "Vpiši odsotnost",
        variant: "warning",
      })
      if (!confirmed) {
        return null
      }
    }
    startLoading("setAbsencesAsync")
    try {
      const raw = await fetchAuthAsync(`${AZURE_URI}/Absences`, "PUT", {
        workerId,
        dates: dates.map((d) => d.format("YYYY-MM-DD")),
        type: mode === "Present" ? null : mode,
      })
      if (raw?.status !== "ok") {
        throw raw
      }
      if (raw.message) {
        setSuccess(raw.message)
      }
      return raw
    } catch (e) {
      setError(e)
      return null
    } finally {
      stopLoading("setAbsencesAsync")
    }
  }

  // one worker's day: { worker, transports (entered), trips (detected WorkerTrips), waypoints (plan) }
  const loadAbsenceDayAsync = async (workerId, date) => {
    startLoading("loadAbsenceDayAsync")
    try {
      const raw = await fetchAuthAsync(
        `${AZURE_URI}/Absences/Day?workerId=${workerId}&date=${date.format("YYYY-MM-DD")}`,
        "GET",
      )
      if (raw?.status !== "ok") {
        throw raw
      }
      return raw
    } catch (e) {
      setError(e)
      return null
    } finally {
      stopLoading("loadAbsenceDayAsync")
    }
  }

  return { loadAbsencesAsync, setAbsencesAsync, loadAbsenceDayAsync }
}

// "YYYY-MM-DD" (DateOnly from the API) as a local date
export const toDay = (value) => dayjs(value)
